import React, { useEffect, useState } from 'react';
import CryptoJS from 'crypto-js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { PAYMENT_AES_IV, PAYMENT_AES_KEY } from '../config/repayPayment';
import './payment-success.css';

const aesKey = CryptoJS.enc.Utf8.parse(PAYMENT_AES_KEY);
const aesIv = CryptoJS.enc.Utf8.parse(PAYMENT_AES_IV);

const formatAmount = (value) => {
  const num = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value || '0.00');
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const processData = (rawData) => {
  if (!rawData) return null;
  try {
    let decoded = rawData;
    while (decoded.includes('%')) {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    }

    const trimmed = decoded.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      return trimmed;
    }

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Base64.parse(decoded) },
      aesKey,
      { iv: aesIv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    const result = decrypted.toString(CryptoJS.enc.Utf8);
    return result || null;
  } catch (e) {
    console.error('Payment response processing error:', e);
    return null;
  }
};

const detectGateway = (data, respCodeParam) => {
  if (data?.GATEWAYNAME || data?.BANKNAME || data?.mid || data?.MID) {
    return 'PayTM';
  }
  if (data?.key || data?.mihpayid || data?.mode || data?.txnid) {
    return 'PayU';
  }
  if (typeof respCodeParam === 'string' && respCodeParam.includes('%')) {
    return 'PayU';
  }
  return 'PayTM';
};

const isTxnSuccess = (data) => {
  const status = String(data?.response_status || data?.status || '').toLowerCase();
  return (
    status === 'txn_success' ||
    status === 'success' ||
    String(data?.response_status || '') === 'TXN_SUCCESS'
  );
};

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [decryptedMsg, setDecryptedMsg] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState({
    payment_type: 'Checking...',
    leadId: 'N/A',
    loanNo: 'N/A',
    loanAmount: '0.00',
    transactionDateTime: '',
  });

  const handleReloan = () => {
    const confirmAction = window.confirm(
      'Are you sure you want to proceed for a Re-loan?'
    );
    if (!confirmAction) return;
    navigate('/apply-now');
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const responseParam = params.get('response');
      const respCodeParam = params.get('respcode');

      const formattedDateTime = new Date()
        .toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        })
        .replace(',', '');

      let finalDetails = {
        payment_type: 'PayU',
        transactionDateTime: formattedDateTime,
        leadId: 'N/A',
        loanNo: 'N/A',
        loanAmount: '0.00',
      };
      let currentStatus = false;

      if (respCodeParam) {
        const processedResult = processData(respCodeParam);
        if (processedResult) {
          try {
            const bracket = processedResult.indexOf('[');
            const brace = processedResult.indexOf('{');
            let jsonStart = -1;
            if (bracket !== -1 && brace !== -1) jsonStart = Math.min(bracket, brace);
            else jsonStart = bracket !== -1 ? bracket : brace;

            const cleanJson =
              jsonStart !== -1 ? processedResult.substring(jsonStart) : processedResult;
            const parsed = JSON.parse(cleanJson);
            const data = Array.isArray(parsed) ? parsed[0] : parsed;

            finalDetails.payment_type = detectGateway(data, respCodeParam);
            currentStatus = isTxnSuccess(data);

            finalDetails = {
              ...finalDetails,
              leadId: data.lead_id || data.leadId || 'N/A',
              loanNo: data.loan_no || data.loanNo || 'N/A',
              loanAmount: data.loan_amount || data.loanAmount || data.amount || '0.00',
            };
          } catch (e) {
            console.error('Payment JSON parse error:', e);
          }
        }
      }

      if (responseParam) {
        const msgResult = processData(responseParam);
        if (msgResult) {
          const msg = msgResult.includes('RESPMSG=')
            ? msgResult.split('RESPMSG=')[1].split('|')[0]
            : msgResult;
          setDecryptedMsg(msg);
        }
      }

      setIsSuccess(currentStatus);
      setPaymentDetails(finalDetails);
    } catch {
      setDecryptedMsg('Error loading payment details.');
    } finally {
      setLoading(false);
    }
  }, [location.search]);

  if (loading) {
    return (
      <section className="ps-section">
        <div className="ps-bg" aria-hidden="true">
          <div className="ps-bg-gradient" />
          <div className="ps-bg-dots" />
        </div>
        <div className="ps-wrap">
          <div className="ps-card ps-card--loading">Loading payment status…</div>
        </div>
      </section>
    );
  }

  return (
    <section className="ps-section">
      <div className="ps-bg" aria-hidden="true">
        <div className="ps-bg-gradient" />
        <div className="ps-bg-dots" />
        <span className="ps-bg-orb ps-bg-orb--1" />
        <span className="ps-bg-orb ps-bg-orb--2" />
      </div>

      <div className="ps-wrap">
        <div className={`ps-card ${isSuccess ? 'ps-card--success' : 'ps-card--fail'}`}>
          {isSuccess ? (
            <>
              <FaCheckCircle className="ps-icon ps-icon--success" aria-hidden="true" />
              <h1 className="ps-heading">Payment Successful</h1>
              <p className="ps-text">
                Great news! Your payment via <strong>{paymentDetails.payment_type}</strong>{' '}
                has been successfully processed.
              </p>
            </>
          ) : (
            <>
              <FaTimesCircle className="ps-icon ps-icon--fail" aria-hidden="true" />
              <h1 className="ps-heading">Payment Failed</h1>
              <p className="ps-text ps-text--fail">
                We couldn&apos;t process your <strong>{paymentDetails.payment_type}</strong>{' '}
                payment: {decryptedMsg || 'Transaction Declined'}
              </p>
            </>
          )}

          <div className="ps-details">
            <div className="ps-row">
              <span>Lead ID</span>
              <strong>{paymentDetails.leadId}</strong>
            </div>
            <div className="ps-row">
              <span>Loan No</span>
              <strong>{paymentDetails.loanNo}</strong>
            </div>
            <div className="ps-row">
              <span>Amount</span>
              <strong>₹{formatAmount(paymentDetails.loanAmount)}</strong>
            </div>
            <div className="ps-row">
              <span>Date &amp; Time</span>
              <strong>{paymentDetails.transactionDateTime}</strong>
            </div>
          </div>

          <div className="ps-divider" />

          {isSuccess ? (
            <button type="button" className="ps-btn" onClick={handleReloan}>
              Click Here for Reloan
            </button>
          ) : (
            <Link to="/" className="ps-btn">
              Go to Home
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default PaymentSuccess;
